import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import BaseDialog from './BaseDialog'
import DialogFooter from './DialogFooter'
import type { DialogTone, ModalVariant } from './modalVariants'
import type { DialogSize } from '../../../theme/dialogs'

/**
 * The single confirmation dialog. Built on `BaseDialog`, it replaces the
 * four near-duplicate confirm patterns (Disconnect / XeroReauth /
 * DeleteUser / ClearData) plus the inline `window.confirm` / one-off
 * `Dialog` confirms scattered through the app.
 *
 * Tone drives the primary button colour and the header icon variant
 * (`neutral` -> `question`, `warning` -> `alert`, `danger` -> `destructive`).
 * Pass `variant` to override the header icon without changing the button colour
 * (e.g. a neutral Export confirm that wants the green `confirmation` icon).
 *
 * Optional friction (all gate the primary action AND the Ctrl/Cmd+Enter
 * submit through one shared predicate):
 *   - `acknowledgement` - a checkbox the user must tick
 *   - `confirmText` - a string the user must type (trim + case-insensitive)
 *   - `confirmDisabled` - parent-driven escape hatch for gates the two
 *     above can't express (e.g. ClearData needs >=1 system + a reason)
 */
// Back-compat alias - the canonical type now lives in `modalVariants` as
// `DialogTone`, shared with `DialogFooter`.
export type ConfirmTone = DialogTone

export interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => Promise<void> | void
  title: string
  titleAttr?: string
  subtitle?: React.ReactNode
  body: React.ReactNode
  tone?: ConfirmTone
  /** Override the header icon variant (defaults derive from `tone`). */
  variant?: ModalVariant
  confirmLabel?: string
  cancelLabel?: string
  pending?: boolean
  /** Checkbox label - confirm stays disabled until ticked. */
  acknowledgement?: React.ReactNode
  /** User must type this verbatim (trimmed, case-insensitive) to confirm. */
  confirmText?: string
  /** Label for the confirm-text input. */
  confirmTextLabel?: string
  /** Parent-driven extra gate, AND-ed with the internal gates. */
  confirmDisabled?: boolean
  /** Error shown as an Alert above the footer. */
  errorText?: string | null
  /** Controls backdrop-click + close icon. Defaults to true. */
  dismissable?: boolean
  /** Width preset. Defaults to 'sm'; widen (e.g. 'md') for count-rich bodies. */
  size?: DialogSize
}

// The header icon now carries the tone (via the variant badge), so the body no
// longer renders an inline tone icon.
const TONE_VARIANT: Record<ConfirmTone, ModalVariant> = {
  neutral: 'question',
  warning: 'alert',
  danger: 'destructive',
}

const normalise = (value: string) => value.trim().toLowerCase()

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  titleAttr,
  subtitle,
  body,
  tone = 'neutral',
  variant,
  confirmLabel,
  cancelLabel = 'Cancel',
  pending = false,
  acknowledgement,
  confirmText,
  confirmTextLabel,
  confirmDisabled = false,
  errorText,
  dismissable = true,
  size = 'sm',
}) => {
  const [acknowledged, setAcknowledged] = useState(false)
  const [typed, setTyped] = useState('')

  // Reset friction state whenever the dialog closes so a re-open starts clean.
  useEffect(() => {
    if (!open) {
      setAcknowledged(false)
      setTyped('')
    }
  }, [open])

  const ackOk = acknowledgement ? acknowledged : true
  const matchOk = confirmText ? normalise(typed) === normalise(confirmText) : true
  // The one shared gate - used by both the button and Ctrl/Cmd+Enter so
  // they can never diverge.
  const canConfirm = !pending && !confirmDisabled && ackOk && matchOk

  const handleConfirm = () => {
    if (!canConfirm) return
    void onConfirm()
  }

  const resolvedConfirmLabel = confirmLabel ?? 'Confirm'
  const headerVariant = variant ?? TONE_VARIANT[tone]

  const confirmInput = confirmText ? (
    <TypedConfirmField
      value={typed}
      onChange={setTyped}
      expected={confirmText}
      label={confirmTextLabel ?? `Type ${confirmText} to confirm`}
      disabled={pending}
    />
  ) : null

  const footer = (
    <DialogFooter
      cancel={{ label: cancelLabel, onClick: onClose, disabled: pending }}
      primary={{
        label: resolvedConfirmLabel,
        onClick: handleConfirm,
        tone,
        pending,
        disabled: !canConfirm,
      }}
    />
  )

  return (
    <BaseDialog
      open={open}
      onClose={onClose}
      title={title}
      titleAttr={titleAttr}
      subtitle={subtitle}
      variant={headerVariant}
      size={size}
      dismissable={dismissable && !pending}
      onSubmit={handleConfirm}
      footer={footer}
    >
      <Stack spacing={1.75}>
        {/* Wrap in a single block so a multi-part body fragment (text +
            <strong>/<em>) renders inline. Without this the bare fragment
            becomes one flex item per node in the column Stack, exploding the
            sentence into spaced vertical fragments. The tone icon now lives in
            the header badge, so the body is plain. */}
        <Box sx={{ minWidth: 0 }}>{body}</Box>

        {acknowledgement && (
          <FormControlLabel
            sx={{ mx: 0, alignItems: 'flex-start' }}
            control={
              <Checkbox
                checked={acknowledged}
                onChange={(e) => setAcknowledged(e.target.checked)}
                disabled={pending}
                size="small"
                sx={{ pt: 0 }}
              />
            }
            label={
              <Typography variant="body2">{acknowledgement}</Typography>
            }
          />
        )}

        {confirmInput}

        {errorText && (
          <Alert severity="error" sx={{ mt: 0.5 }}>
            {errorText}
          </Alert>
        )}
      </Stack>
    </BaseDialog>
  )
}

interface TypedConfirmFieldProps {
  value: string
  onChange: (next: string) => void
  expected: string
  label: string
  disabled?: boolean
}

const TypedConfirmField: React.FC<TypedConfirmFieldProps> = ({
  value,
  onChange,
  expected,
  label,
  disabled,
}) => {
  const mismatch = value !== '' && normalise(value) !== normalise(expected)
  return (
    <Box>
      {/* Label-at-top (not a floating MUI label) so the confirm step reads as a
          deliberate, clearly-headed field. */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {label}
      </Typography>
      <TextField
        fullWidth
        size="small"
        placeholder={expected}
        value={value}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChange(e.target.value)}
        error={mismatch}
        helperText={mismatch ? 'Does not match' : ' '}
        disabled={disabled}
      />
    </Box>
  )
}

export default ConfirmDialog
